/**
 * One rubric in the catalog: its own fields, plus the levels sub-form.
 *
 * ONE RUBRIC TYPE (B10) — no kind, no dispatch, no second sub-form. Every rubric is the
 * tutor-facing scored one: `name`, `instructions`, `levels`. It no longer carries `standards`
 * (B12.1.2) — those live on the activity, in `StandardSelect`.
 *
 * SCOPE: everything here edits `data.rubrics`, the CATALOG MAP. Nothing reads or writes
 * `Activity.rubrics`, which is an array of ids (B8).
 *
 * No function below mutates the document — each is a thin wrapper over a store action (B9). The
 * rubric arrives as a GETTER: the catalog is a map the author can add to while a card is open.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { ReviewRubric } from './types'

import { pluralize, withCount } from '@shared/lib/format'

import { useAssessmentEditorStore } from './store'
import { RUBRIC_LEVEL_SCORES } from '../../lib/assessments/schema'

/** Membership test for `refExtraLevelsCount`, built once rather than per level per render. */
const FIXED_SCORES = new Set<number>(RUBRIC_LEVEL_SCORES)

export interface RubricEditorProps {
  rubricId: string
}

interface UseRubricEditorOptions {
  rubricId: () => string
}

interface UseRubricEditorReturn {
  refRubric: ComputedRef<ReviewRubric | null>
  refName: ComputedRef<string>
  setName: (value: string) => void
}

/** The top half of a rubric card: just `name` now. `useRubricLevels` below is the rest of the same
 *  card — one rubric type, so it is rendered unconditionally, never dispatched to. */
export const useRubricEditor = (
  { rubricId }: UseRubricEditorOptions,
): UseRubricEditorReturn => {
  const store = useAssessmentEditorStore()

  const refRubric = computed<ReviewRubric | null>(() =>
    store.getRubricCatalog[rubricId()] ?? null,
  )

  const refName = computed<string>(() => refRubric.value?.name ?? '')

  const setName = (value: string): void => {
    store.updateRubric(rubricId(), { name: value })
  }

  return {
    refRubric,
    refName,
    setName,
  }
}

// --- the levels grid — THREE FIXED BANDS -----------------------------------
//
// The bands are `RUBRIC_LEVEL_SCORES` (0, 1, 2): no add, no remove, no reorder, no score field.
// The rows come from the CONSTANT, not from the document, so a stored rubric carrying only band 1
// still shows three rows (`setRubricLevel` inserts in score order). A stored band outside 0–2 is
// NOT deleted (B9) and cannot be shown, so it is counted and reported — `refExtraLevelsCount`.

/** One of the three fixed bands. `score` is both the band's identity and its address in the store
 *  — there is no index here, because a stored rubric's array order is not the UI's. */
interface RubricLevelItem {
  score: number
  description: string
}

interface UseRubricLevelsReturn {
  refInstructions: ComputedRef<string>
  /** Always the three bands, HIGHEST FIRST (2, 1, 0), for any rubric that carries a `levels` array
   *  — an unwritten band renders as an empty row. Empty only for a rubric this editor cannot
   *  author (see `refIsAuthorable`). */
  refLevels: ComputedRef<RubricLevelItem[]>
  /** False for a rubric with no `levels` array at all — an observation rubric predating B10.
   *  Nothing here repairs it; validation rejects it on save. */
  refIsAuthorable: ComputedRef<boolean>
  /** How many stored bands fall outside `RUBRIC_LEVEL_SCORES` — non-zero only for a rubric authored
   *  before the bands were fixed. Those bands are kept as authored (B9) and surfaced rather than
   *  hidden, since the editor cannot show them. */
  refExtraLevelsCount: ComputedRef<number>
  /** The sentence that reports them. Empty when there are none, so the card renders nothing. */
  refExtraLevelsNote: ComputedRef<string>
  setInstructions: (value: string) => void
  setLevelDescription: (score: number, value: string) => void
}

export const useRubricLevels = (
  { rubricId }: UseRubricEditorOptions,
): UseRubricLevelsReturn => {
  const store = useAssessmentEditorStore()

  const refRubric = computed<ReviewRubric | null>(() =>
    store.getRubricCatalog[rubricId()] ?? null,
  )

  /** `Array.isArray`, not `?? []`: the working document is raw (B9) and may hold a rubric this
   *  editor cannot author — an observation rubric predating B10 has `prompts` and no `levels`. It
   *  renders as an empty levels list and validation rejects it on save; nothing here repairs it. */
  const refRawLevels = computed<ReviewRubric['levels']>(() =>
    Array.isArray(refRubric.value?.levels) ? refRubric.value.levels : [],
  )

  const refInstructions = computed<string>(() => refRubric.value?.instructions ?? '')

  const refIsAuthorable = computed<boolean>(() => Array.isArray(refRubric.value?.levels))

  /**
   * The three fixed bands, always all three — the rendered rows come from `RUBRIC_LEVEL_SCORES`,
   * not from the document, so a rubric missing a band shows it empty and ready to type into.
   *
   * DESCENDING (2, 1, 0) is display order only: an author writes what full marks look like first.
   * `setRubricLevel` still inserts in ascending score order, so the stored array reads 0, 1, 2.
   */
  const refLevels = computed<RubricLevelItem[]>(() => {
    if (!refIsAuthorable.value) {
      return []
    }
    return [...RUBRIC_LEVEL_SCORES].reverse().map((score) => {
      const stored = refRawLevels.value.find(level => level.score === score)
      return {
        score,
        description: stored?.description ?? '',
      }
    })
  })

  const refExtraLevelsCount = computed<number>(() =>
    refRawLevels.value.filter(level => !FIXED_SCORES.has(level.score)).length,
  )

  const refExtraLevelsNote = computed<string>(() => {
    const count = refExtraLevelsCount.value
    if (!count) {
      return ''
    }
    const subject = withCount(count, { one: 'stored level', other: 'stored levels' })
    const verb = pluralize(count, { one: 'is', other: 'are' })

    return `${subject} outside 0–2 ${verb} kept as authored and not shown here.`
  })

  /** Optional — an emptied box deletes the key rather than storing `''`. */
  const setInstructions = (value: string): void => {
    store.updateRubric(rubricId(), { instructions: value.trim() ? value : null })
  }

  /** Optional in the schema — emptied deletes the key rather than storing `''`. */
  const setLevelDescription = (score: number, value: string): void => {
    store.setRubricLevel(rubricId(), score, { description: value.trim() ? value : null })
  }

  return {
    refInstructions,
    refLevels,
    refIsAuthorable,
    refExtraLevelsCount,
    refExtraLevelsNote,
    setInstructions,
    setLevelDescription,
  }
}
